import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Avatar,
  Paper,
  Container,
  Stack,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import {
  ArrowBackIos,
  ArrowForwardIos,
  Close,
  CalendarToday,
  AccessTime,
  LocalShipping,
  CheckCircle,
  Cancel,
  DirectionsCar,
  InfoOutlined,
} from "@mui/icons-material";
import RouteTimeline from "../Report/DriversReport/DriversReportTracker/components/RouteTimeline";

// Generate mock data for a month
const generateMonthData = (year, month, driverId, driverName) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const days = [];
  const currentDate = new Date();

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateStr = date.toISOString().split("T")[0];

    // Skip future dates
    if (date > currentDate) {
      days.push({
        date: dateStr,
        dayNumber: day,
        status: "upcoming",
        shift: null,
        totalOrders: 0,
        amOrders: 0,
        pmOrders: 0,
        deliveredOrders: 0,
        failedOrders: 0,
        totalStops: 0,
        hoursWorked: 0,
      });
      continue;
    }

    // Random rest days (10% chance)
    if (Math.random() < 0.1) {
      days.push({
        date: dateStr,
        dayNumber: day,
        status: "rest",
        shift: null,
        totalOrders: 0,
        amOrders: 0,
        pmOrders: 0,
        deliveredOrders: 0,
        failedOrders: 0,
        totalStops: 0,
        hoursWorked: 0,
      });
      continue;
    }

    // Determine shift
    const shiftType = Math.random();
    let shift,
      amOrders = 0,
      pmOrders = 0,
      hoursWorked = 0;

    if (shiftType < 0.3) {
      shift = "AM";
      amOrders = Math.floor(Math.random() * 20) + 15; // 15-35 orders
      hoursWorked = Math.floor(Math.random() * 2) + 5; // 5-7 hours
    } else if (shiftType < 0.6) {
      shift = "PM";
      pmOrders = Math.floor(Math.random() * 20) + 15;
      hoursWorked = Math.floor(Math.random() * 2) + 5;
    } else {
      shift = "AM/PM";
      amOrders = Math.floor(Math.random() * 20) + 15;
      pmOrders = Math.floor(Math.random() * 20) + 15;
      hoursWorked = Math.floor(Math.random() * 3) + 9; // 9-12 hours
    }

    const totalOrders = amOrders + pmOrders;
    const failedOrders = Math.floor(Math.random() * 3); // 0-2 failed
    const deliveredOrders = totalOrders - failedOrders;
    const totalStops = Math.floor(totalOrders / 2.5); // Roughly 2.5 orders per stop

    days.push({
      date: dateStr,
      dayNumber: day,
      status: "completed",
      shift,
      totalOrders,
      amOrders,
      pmOrders,
      deliveredOrders,
      failedOrders,
      totalStops,
      hoursWorked,
      startTime: shift.includes("AM") ? "08:00" : "14:00",
      endTime: shift === "AM/PM" ? "20:00" : shift === "AM" ? "14:00" : "20:00",
      delivery_routes: generateRoutesSummary(
        totalStops,
        totalOrders,
        deliveredOrders,
        failedOrders
      ),
    });
  }

  return {
    year,
    month,
    driverId,
    driverName,
    firstDayOfWeek: firstDay,
    daysInMonth,
    days,
  };
};

const generateRoutesSummary = (
  stopCount,
  totalOrders,
  deliveredOrders,
  failedOrders
) => {
  const areas = [
    "Thamel",
    "Boudha",
    "Patan",
    "Bhaktapur",
    "Kirtipur",
    "Balaju",
    "Sankhamul",
    "Pulchowk",
    "Koteshwor",
    "Chabahil",
  ];

  const customers = [
    "Ram Sharma",
    "Sita Poudel",
    "Hari Thapa",
    "Gita Maharjan",
    "Krishna Shrestha",
    "Laxmi Tamang",
    "Bikash Rai",
    "Anita Gurung",
    "Pramod Karki",
    "Sunita Shahi",
  ];

  const delivery_routes = [];
  let orderCounter = 1;
  let remainingOrders = totalOrders;
  let remainingDelivered = deliveredOrders;
  let remainingFailed = failedOrders;

  for (let i = 0; i < stopCount; i++) {
    // Distribute orders across stops
    const ordersAtStop = Math.min(
      Math.ceil(remainingOrders / (stopCount - i)),
      Math.floor(Math.random() * 3) + 1
    );

    const orders = [];
    for (let j = 0; j < ordersAtStop; j++) {
      let orderState = "Loaded";
      let stopState = "pending";

      // Determine order state based on remaining delivered/failed
      if (remainingDelivered > 0 && Math.random() > 0.2) {
        orderState = "Delivered";
        remainingDelivered--;
        stopState = "completed";
      } else if (remainingFailed > 0 && Math.random() > 0.7) {
        orderState = "Failed";
        remainingFailed--;
        stopState = "completed";
      }

      const orderId = `ORD-${String(orderCounter++).padStart(3, "0")}`;
      const lineItemsCount = Math.floor(Math.random() * 3) + 1;
      const line_items = Array.from({ length: lineItemsCount }, (_, k) => ({
        item_code: `${orderId}-${String.fromCharCode(65 + k)}`,
        item_description: `Auto Part ${orderId}-${String.fromCharCode(65 + k)}`,
        item_loaded_qty: "1",
        item_loaded_date_time: "30 Dec, 2025 10:00 AM",
      }));

      orders.push({
        order_number: orderId,
        customer_name: customers[Math.floor(Math.random() * customers.length)],
        customer_phone: 9800000000 + Math.floor(Math.random() * 99999999),
        order_sate: orderState,
        order_tag: Math.random() > 0.8 ? "Urgent" : "",
        line_items: line_items,
      });
    }

    const deliveredCount = orders.filter(
      (o) => o.order_sate === "Delivered"
    ).length;
    const failedCount = orders.filter((o) => o.order_sate === "Failed").length;

    // Determine overall stop state
    let stopState = "pending";
    if (deliveredCount + failedCount === orders.length && orders.length > 0) {
      stopState = "completed";
    } else if (deliveredCount > 0 || failedCount > 0) {
      stopState = "current";
    }

    delivery_routes.push({
      stop_number: i + 1,
      stop_name: `${areas[i % areas.length]}, Kathmandu`,
      stop_state: stopState,
      total_order: orders.length,
      total_delivered_order: deliveredCount,
      total_failed_order: failedCount,
      order_list: orders,
    });

    remainingOrders -= ordersAtStop;
  }

  return delivery_routes;
};

const DayCard = ({ day, onClick }) => {
  if (!day) {
    return (
      <Box
        sx={{
          aspectRatio: "1",
          border: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
        }}
      />
    );
  }

  const isUpcoming = day.status === "upcoming";
  const isRest = day.status === "rest";
  const isToday = day.date === new Date().toISOString().split("T")[0];

  return (
    <Box
      onClick={() => !isUpcoming && onClick(day)}
      sx={{
        aspectRatio: "1",
        border: "1px solid",
        borderRadius: 2,
        p: 1,
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        ...(isUpcoming
          ? {
              bgcolor: "#fafafa",
              borderColor: "#e0e0e0",
              cursor: "not-allowed",
            }
          : isRest
          ? {
              bgcolor: "#e3f2fd",
              borderColor: "#90caf9",
              cursor: "pointer",
              "&:hover": {
                boxShadow: 2,
                borderColor: "#1976d2",
              },
            }
          : {
              bgcolor: "white",
              borderColor: "#bdbdbd",
              cursor: "pointer",
              "&:hover": {
                boxShadow: 2,
                borderColor: "#1976d2",
              },
            }),
        ...(isToday && { border: "2px solid", borderColor: "#1976d2" }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{
            color: isUpcoming ? "#9e9e9e" : isToday ? "#1976d2" : "#212121",
          }}
        >
          {day.dayNumber}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {!isUpcoming && !isRest && (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircle sx={{ fontSize: 16, color: "#4caf50" }} />
                      <Typography variant="body2" sx={{ color: "#fff" }}>
                        <strong>{day.deliveredOrders}</strong> Orders Delivered
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccessTime sx={{ fontSize: 16, color: "#ff9800" }} />
                      <Typography variant="body2" sx={{ color: "#fff" }}>
                        <strong>{day.hoursWorked}</strong> Hours Worked
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(33, 33, 33, 0.95)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    borderRadius: 2,
                    "& .MuiTooltip-arrow": {
                      color: "rgba(33, 33, 33, 0.95)",
                    },
                  },
                },
              }}
            >
              <InfoOutlined
                sx={{
                  fontSize: 14,
                  color: "#757575",
                  cursor: "pointer",
                  "&:hover": { color: "#1976d2" },
                }}
              />
            </Tooltip>
          )}
          {isToday && (
            <Chip label="Today" size="small" color="primary" variant="filled" />
          )}
        </Stack>
      </Stack>

      {isRest ? (
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Typography variant="caption" color="primary" fontWeight="medium">
            No deliveries
          </Typography>
        </Stack>
      ) : isUpcoming ? (
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Typography variant="caption" color="#9e9e9e">
            -
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={0.5} flex={1} justifyContent="space-between">
          {/* Orders - Show breakdown by shift */}
          <Box>
            {day.shift === "AM" && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocalShipping fontSize="small" sx={{ color: "#ef6c00" }} />
                <Typography variant="caption" fontWeight="bold" color="#ef6c00">
                  {day.amOrders} AM
                </Typography>
              </Stack>
            )}
            {day.shift === "PM" && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocalShipping fontSize="small" sx={{ color: "#7b1fa2" }} />
                <Typography variant="caption" fontWeight="bold" color="#7b1fa2">
                  {day.pmOrders} PM
                </Typography>
              </Stack>
            )}
            {day.shift === "AM/PM" && (
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocalShipping fontSize="small" sx={{ color: "#ef6c00" }} />
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="#ef6c00"
                  >
                    {day.amOrders} AM
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocalShipping fontSize="small" sx={{ color: "#7b1fa2" }} />
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="#7b1fa2"
                  >
                    {day.pmOrders} PM
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>

          {/* Hours */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AccessTime fontSize="small" sx={{ color: "#9e9e9e" }} />
            <Typography variant="caption" color="#757575">
              {day.hoursWorked}h
            </Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const DetailModal = ({ day, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!day) return null;

  return (
    <Dialog
      fullScreen={fullScreen}
      open={!!day}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#f5f5f5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {new Date(day.date).toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Delivery Report
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "100%" }}>
        <Box p={3} height="100%" overflow="auto">
          {/* Summary Cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2, textAlign: "center", bgcolor: "#e3f2fd" }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  mb={1}
                >
                  <LocalShipping sx={{ color: "#1976d2" }} />
                  <Typography
                    variant="caption"
                    color="primary"
                    fontWeight="medium"
                  >
                    Total Orders
                  </Typography>
                </Stack>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {day.totalOrders}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e8" }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  mb={1}
                >
                  <CheckCircle sx={{ color: "#2e7d32" }} />
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight="medium"
                  >
                    Delivered
                  </Typography>
                </Stack>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {day.deliveredOrders}
                </Typography>
              </Paper>
            </Grid>

            {day.failedOrders > 0 && (
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "#ffebee" }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    mb={1}
                  >
                    <Cancel sx={{ color: "#c62828" }} />
                    <Typography
                      variant="caption"
                      color="error.main"
                      fontWeight="medium"
                    >
                      Failed
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {day.failedOrders}
                  </Typography>
                </Paper>
              </Grid>
            )}

            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2, textAlign: "center", bgcolor: "#f3e5f5" }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  mb={1}
                >
                  <DirectionsCar sx={{ color: "#7b1fa2" }} />
                  <Typography
                    variant="caption"
                    color="secondary.main"
                    fontWeight="medium"
                  >
                    Stops
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="secondary.main"
                >
                  {day.totalStops}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Shift Details */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Shift Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  Shift Type
                </Typography>
                <Chip
                  label={day.shift}
                  size="small"
                  sx={{
                    ...(day.shift === "AM"
                      ? { bgcolor: "#fff3e0", color: "#ef6c00" }
                      : day.shift === "PM"
                      ? { bgcolor: "#f3e5f5", color: "#7b1fa2" }
                      : { bgcolor: "#e8f5e8", color: "#2e7d32" }),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  Working Hours
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AccessTime sx={{ color: "#616161" }} />
                  <Typography fontWeight="bold">
                    {day.hoursWorked} hours
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {day.startTime} - {day.endTime}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Orders Breakdown */}
          {day.shift === "AM/PM" && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Orders Breakdown
              </Typography>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fff3e0" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{ width: 12, height: 12, bgcolor: "#ef6c00" }}
                      />
                      <Typography fontWeight="medium">
                        Morning Shift (AM)
                      </Typography>
                    </Stack>
                    <Typography fontWeight="bold" color="#ef6c00">
                      {day.amOrders} orders
                    </Typography>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f3e5f5" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{ width: 12, height: 12, bgcolor: "#7b1fa2" }}
                      />
                      <Typography fontWeight="medium">
                        Evening Shift (PM)
                      </Typography>
                    </Stack>
                    <Typography fontWeight="bold" color="#7b1fa2">
                      {day.pmOrders} orders
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          )}

          {/* Routes Coverage */}
          {day.delivery_routes && day.delivery_routes.length > 0 && (
            <Box>
              <RouteTimeline
                stops={day.delivery_routes.map((route) => ({
                  stopId: `STOP-${String(route.stop_number).padStart(2, "0")}`,
                  stopNumber: route.stop_number,
                  location: route.stop_name,
                  address: route.stop_name,
                  status: route.stop_state,
                  orders: route.order_list.map((order) => ({
                    orderId: order.order_number,
                    customerName: order.customer_name,
                    phone: `+977-${order.customer_phone}`,
                    status:
                      order.order_sate === "Delivered"
                        ? "delivered"
                        : order.order_sate === "Failed"
                        ? "failed"
                        : order.order_sate === "At Location"
                        ? "at_location"
                        : "loaded",
                    priority:
                      order.order_tag === "Urgent" ? "urgent" : "normal",
                    items: order.line_items.map((item) => ({
                      code: item.item_code,
                      description: item.item_description,
                      quantity: item.item_loaded_qty,
                    })),
                    notes:
                      order.order_sate === "Failed" ? "Delivery failed" : null,
                  })),
                  timeline: [],
                  estimatedArrival: null,
                }))}
                currentStop={null}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const DeliveryReportModal = ({ driver }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthData = generateMonthData(
    year,
    month,
    driver?.id || "D123",
    driver?.name || "John Doe"
  );

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(year, month + 1, 1);
    // Don't allow future months
    if (nextMonth <= new Date()) {
      setCurrentDate(nextMonth);
    }
  };

  const monthName = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Calculate calendar grid
  const calendarDays = [];
  // Add empty cells for days before month starts
  for (let i = 0; i < monthData.firstDayOfWeek; i++) {
    calendarDays.push(null);
  }
  // Add all days of the month
  calendarDays.push(...monthData.days);

  // Calculate monthly statistics
  const completedDays = monthData.days.filter((d) => d.status === "completed");
  const totalOrders = completedDays.reduce((sum, d) => sum + d.totalOrders, 0);
  const totalDelivered = completedDays.reduce(
    (sum, d) => sum + d.deliveredOrders,
    0
  );
  const totalHours = completedDays.reduce((sum, d) => sum + d.hoursWorked, 0);
  const workingDays = completedDays.length;

  return (
    <Container maxWidth={false} sx={{ height: "100%", p: 2 }}>
      {/* Header */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Monthly Delivery Report
            </Typography>
            <Typography variant="body2" color="textSecondary" mt={0.5}>
              Driver: {monthData.driverName}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarToday color="action" />
          </Stack>
        </Stack>

        {/* Monthly Stats */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: "center", bgcolor: "#e3f2fd" }}
            >
              <Typography
                variant="caption"
                color="primary"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Total Orders
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {totalOrders}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e8" }}
            >
              <Typography
                variant="caption"
                color="success.main"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Delivered
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {totalDelivered}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: "center", bgcolor: "#f3e5f5" }}
            >
              <Typography
                variant="caption"
                color="secondary.main"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Working Days
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {workingDays}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: "center", bgcolor: "#fff3e0" }}
            >
              <Typography
                variant="caption"
                color="warning.main"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Total Hours
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {totalHours}h
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendar */}
      <Paper variant="outlined" sx={{ p: 2, height: "100%", overflow: "auto" }}>
        {/* Month Navigation with Year and Month Selectors */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          spacing={2}
        >
          <IconButton onClick={handlePrevMonth} size="small">
            <ArrowBackIos />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flex={1}
            justifyContent="center"
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) =>
                  setCurrentDate(new Date(e.target.value, month, 1))
                }
              >
                {Array.from(
                  { length: 10 },
                  (_, i) => new Date().getFullYear() - i
                ).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                label="Month"
                onChange={(e) =>
                  setCurrentDate(new Date(year, e.target.value, 1))
                }
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((monthName, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {monthName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <IconButton
            onClick={handleNextMonth}
            disabled={new Date(year, month + 1, 1) > new Date()}
            size="small"
          >
            <ArrowForwardIos />
          </IconButton>
        </Stack>

        {/* Week Days Header */}
        <Grid container spacing={1} mb={1}>
          {weekDays.map((day) => (
            <Grid
              key={day}
              item
              xs={1.714}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="textSecondary"
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Grid */}
        <Grid container spacing={1}>
          {calendarDays.map((day, idx) => (
            <Grid key={idx} item xs={1.714}>
              <DayCard day={day} onClick={setSelectedDay} />
            </Grid>
          ))}
        </Grid>

        {/* Legend */}
        <Divider sx={{ my: 2 }} />
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#fff3e0",
                border: "1px solid #ffcc80",
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              AM Shift
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#f3e5f5",
                border: "1px solid #ce93d8",
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              PM Shift
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#e8f5e8",
                border: "1px solid #a5d6a7",
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              AM/PM Shift
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#e3f2fd",
                border: "1px solid #90caf9",
                borderRadius: 1,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              No deliveries
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Detail Modal */}
      {selectedDay && (
        <DetailModal day={selectedDay} onClose={() => setSelectedDay(null)} />
      )}
    </Container>
  );
};

export default DeliveryReportModal;
